/**
 * Student Query Functions
 *
 * Functions for querying students and missions based on teacher-student relationships.
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { UserDocument, MissionDocument, ProficiencyLevel } from '../../types/firestore';

const USERS_COLLECTION = 'users';
const MISSIONS_COLLECTION = 'missions';

/**
 * Get all students for a specific teacher
 * Returns students where teacherId matches the teacher's UID
 */
export const getStudentsForTeacher = async (
  teacherId: string
): Promise<UserDocument[]> => {
  if (!db) {
    throw new Error('Firebase is not configured. Please check your environment variables.');
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(
      usersRef,
      where('teacherId', '==', teacherId),
      where('role', '==', 'student'),
      orderBy('joinedClassAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as UserDocument);
  } catch (error) {
    console.error('Error fetching students for teacher:', error);
    throw error;
  }
};

/**
 * Get active missions for a student based on their assigned teacher
 *
 * For GROUP students (isPrivateStudent = false/undefined):
 *   - Returns missions filtered by level compatibility
 *   - Excludes missions with assignedStudentIds (those are private-only)
 *
 * For PRIVATE students (isPrivateStudent = true):
 *   - Returns ONLY missions where their studentId is in assignedStudentIds
 *   - No level filtering - they see whatever the teacher assigns
 */
export const getMissionsForStudent = async (
  teacherId: string,
  level?: ProficiencyLevel,
  studentId?: string,
  isPrivateStudent?: boolean
): Promise<MissionDocument[]> => {
  if (!db) {
    throw new Error('Firebase is not configured. Please check your environment variables.');
  }

  if (!teacherId) {
    console.warn('getMissionsForStudent called without teacherId');
    return [];
  }

  try {
    const missionsRef = collection(db, MISSIONS_COLLECTION);

    // Build query constraints
    const constraints: QueryConstraint[] = [
      where('teacherId', '==', teacherId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
    ];

    const q = query(missionsRef, ...constraints);
    const querySnapshot = await getDocs(q);

    let missions = querySnapshot.docs.map(doc => doc.data() as MissionDocument);

    if (isPrivateStudent && studentId) {
      // PRIVATE STUDENT: Only see lessons explicitly assigned to them
      // No level filtering - teacher controls what they see
      missions = missions.filter(mission =>
        mission.assignedStudentIds?.includes(studentId)
      );
    } else {
      // GROUP STUDENT: Level-based filtering + exclude private-only lessons

      // First, filter out lessons that are assigned to specific students (private lessons)
      missions = missions.filter(mission =>
        !mission.assignedStudentIds || mission.assignedStudentIds.length === 0
      );

      // Filter out lessons where showOnHomepage is explicitly false
      missions = missions.filter(mission =>
        mission.showOnHomepage !== false
      );

      // Then apply level filtering if a level is provided
      // Only show missions at the student's EXACT level (or missions with no level set)
      if (level) {
        missions = missions.filter(mission =>
          !mission.targetLevel || mission.targetLevel === level
        );
      }
    }

    return missions;
  } catch (error) {
    console.error('Error fetching missions for student:', error);
    throw error;
  }
};

/**
 * Get count of students for a teacher
 */
export const getStudentCountForTeacher = async (
  teacherId: string
): Promise<number> => {
  if (!db) {
    throw new Error('Firebase is not configured. Please check your environment variables.');
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(
      usersRef,
      where('teacherId', '==', teacherId),
      where('role', '==', 'student')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error counting students for teacher:', error);
    throw error;
  }
};

/**
 * Mission completion stats for teacher dashboard
 */
export interface MissionCompletionStats {
  missionId: string;
  completedCount: number;
  totalEligible: number;
  completionRate: number;
  notCompletedStudents: { uid: string; name: string; level?: ProficiencyLevel }[];
}

/**
 * Get completion stats for a specific mission
 * Returns how many students have completed it and who hasn't
 */
export const getMissionCompletionStats = async (
  teacherId: string,
  missionId: string,
  missionLevel?: ProficiencyLevel
): Promise<MissionCompletionStats> => {
  if (!db) {
    throw new Error('Firebase is not configured. Please check your environment variables.');
  }

  try {
    // Get all students for this teacher
    const students = await getStudentsForTeacher(teacherId);

    // Filter to students who are eligible for this mission (exact level match)
    const eligibleStudents = students.filter(student => {
      if (!missionLevel) return true; // Mission available to all levels
      if (!student.level) return true; // Student without level can access all
      return student.level === missionLevel;
    });

    // Check which students have completed this mission
    const completedStudents: UserDocument[] = [];
    const notCompletedStudents: { uid: string; name: string; level?: ProficiencyLevel }[] = [];

    for (const student of eligibleStudents) {
      const completedMissions = student.uniqueScenariosCompleted || [];
      if (completedMissions.includes(missionId)) {
        completedStudents.push(student);
      } else {
        notCompletedStudents.push({
          uid: student.uid,
          name: student.displayName || student.email || 'Unknown',
          level: student.level,
        });
      }
    }

    const totalEligible = eligibleStudents.length;
    const completedCount = completedStudents.length;
    const completionRate = totalEligible > 0 ? (completedCount / totalEligible) * 100 : 0;

    return {
      missionId,
      completedCount,
      totalEligible,
      completionRate,
      notCompletedStudents,
    };
  } catch (error) {
    console.error('Error fetching mission completion stats:', error);
    throw error;
  }
};

/**
 * Get completion stats for all missions for a teacher
 * Returns a map of missionId -> completion stats
 */
export const getAllMissionCompletionStats = async (
  teacherId: string
): Promise<Record<string, MissionCompletionStats>> => {
  if (!db) {
    throw new Error('Firebase is not configured. Please check your environment variables.');
  }

  try {
    // Get all active missions for this teacher
    const missionsRef = collection(db, MISSIONS_COLLECTION);
    const q = query(
      missionsRef,
      where('teacherId', '==', teacherId),
      where('isActive', '==', true)
    );
    const missionsSnapshot = await getDocs(q);
    const missions = missionsSnapshot.docs.map(doc => doc.data() as MissionDocument);

    // Get all students for this teacher
    const students = await getStudentsForTeacher(teacherId);

    // Build completion stats for each mission
    const statsMap: Record<string, MissionCompletionStats> = {};

    for (const mission of missions) {
      // Filter to students who are eligible for this mission (exact level match)
      const eligibleStudents = students.filter(student => {
        if (!mission.targetLevel) return true;
        if (!student.level) return true;
        return student.level === mission.targetLevel;
      });

      const completedStudents: UserDocument[] = [];
      const notCompletedStudents: { uid: string; name: string; level?: ProficiencyLevel }[] = [];

      for (const student of eligibleStudents) {
        const completedMissions = student.uniqueScenariosCompleted || [];
        if (completedMissions.includes(mission.id)) {
          completedStudents.push(student);
        } else {
          notCompletedStudents.push({
            uid: student.uid,
            name: student.displayName || student.email || 'Unknown',
            level: student.level,
          });
        }
      }

      const totalEligible = eligibleStudents.length;
      const completedCount = completedStudents.length;
      const completionRate = totalEligible > 0 ? (completedCount / totalEligible) * 100 : 0;

      statsMap[mission.id] = {
        missionId: mission.id,
        completedCount,
        totalEligible,
        completionRate,
        notCompletedStudents,
      };
    }

    return statsMap;
  } catch (error) {
    console.error('Error fetching all mission completion stats:', error);
    throw error;
  }
};
